import styles from "./ReviewAndShipPage.module.css";

import { FbaPageProps } from "../FbaShipmentFlow";
import PageContent from "@/components/PageContent";
import PageHeader from "../PageHeader";
import { FbaShipment, getFbaBatchItemById, useShipmentContext } from "@/contexts/ShipmentContext";
import InfoBanner from "@/components/InformationBanner/InfoBanner";
import Button from "@/components/forms/Button";
import IconPrintGoogle from "@/components/icons/IconPrintGoogle";
import { TransportationOption } from "@boxem/core-api-schema";
import RadioInput from "@/components/forms/RadioInput";
import { useEffect, useMemo, useState } from "react";
import ProductView from "../ProductView";
import { BoxHeader } from "../ShipmentBox";
import dayjs from "dayjs";
import { formatValueAsCurrency } from "@/utils/formatUtils";
import IconCheckCircle from "@/components/icons/IconCheckCircle";
import Card, { CardAccordion, CardContent, CollapseButton } from "@/components/Card";
import { usePrinterContext } from "@/contexts/PrinterContext";
import { useUserContext } from "@/contexts/UserContext";
import Tooltip from "@/components/Tooltip";
import IconCopyClipboard from "@/components/icons/IconCopyClipboard";

type CarrierOption = {
  name: string;
  alphaCode: string;
  cost: number;
  startTime: string;
  optionSet: Record<number, string>;
}

type CarrierOptions = {
  name: string;
  alphaCode: string;
  options: CarrierOption[];
}

function findOption(shipment: FbaShipment, option: TransportationOption): TransportationOption {
  return shipment.transportationOptions.find(o => (
    o.carrier.alphaCode === option.carrier.alphaCode
    && o.expiration === option.expiration
    && o.carrierAppointment.startTime === option.carrierAppointment.startTime
    && o.carrierAppointment.endTime === option.carrierAppointment.endTime
  ))
}

export default function ReviewAndShipPage(props: FbaPageProps) {
  const { continueButton, onContinueClicked, onCancelClicked, onError, cloneShipment } = props;
  const { currentBatch, setSelectedTransportationOptions } = useShipmentContext();
  const { printPalletLabel } = usePrinterContext();
  const { addressBook } = useUserContext();

  const carriers = useMemo<CarrierOptions[]>(() => {
    const carriers: { [key: string]: CarrierOption[] } = {};
    outer: for (const option of currentBatch.shipments[0].transportationOptions) {
      if (Date.now() > new Date(option.expiration).valueOf()
        || !option.carrierAppointment.startTime) {
        continue;
      }

      const optionSet = { [currentBatch.shipments[0].id]: option.transportationOptionId };
      let cost = option.cost!;
      for (const shipment of currentBatch.shipments.slice(1)) {
        const matchedOption = findOption(shipment, option);
        if (!matchedOption
          || Date.now() > new Date(matchedOption.expiration).valueOf()
          || !matchedOption.carrierAppointment.startTime) {
          continue outer;
        }

        cost += matchedOption.cost;
        optionSet[shipment.id] = matchedOption.transportationOptionId;
      }
      if (!carriers[option.carrier.alphaCode]) {
        carriers[option.carrier.alphaCode] = [];
      }
      carriers[option.carrier.alphaCode].push({
        alphaCode: option.carrier.alphaCode,
        name: option.carrier.name,
        startTime: option.carrierAppointment.startTime,
        cost,
        optionSet,
      });
    }
    return Object.values(carriers).map(
      options => ({ name: options[0].name, alphaCode: options[0].alphaCode, options })
    );
  }, [currentBatch.shipments]);

  const [selectedOption, _setSelectedOption] = useState<CarrierOption>(null);
  const setSelectedOption = (option: CarrierOption) => {
    _setSelectedOption(option);
    if (option) setSelectedTransportationOptions(option.optionSet);
  }

  async function printPalletLabels() {
    const fromAddress = addressBook.find(address => address.id === currentBatch.fromId);
    const jobs = [];
    const errors = [];
    for (const shipment of currentBatch.shipments) {
      const palletCount = shipment.pallets.length;
      const toAddress = shipment.destinationAddress;
      jobs.push(printPalletLabel(
        fromAddress,
        toAddress,
        shipment.shipmentId,
        shipment.shipmentName,
        shipment.createdAt,
        palletCount*4
      ).catch(e => errors.push(e)));
    }

    await Promise.allSettled(jobs);
    if (errors.length > 0) onError(errors);
  }

  return (
    <PageContent
      className={styles.pageContent}
      header={
        <PageHeader
          step={3}
          shipmentType={currentBatch.shipmentType}
          continueButton={continueButton}
          onContinueClicked={onContinueClicked}
          onCancelClicked={onCancelClicked}
        />
      }
    >
      <InfoBanner
        message={
          <>This step is required before proceeding. The selected carrier will
          <span className="strong"> apply to all shipments</span> in your batch.</>
        }
        storageKey="selectCarrierKey"
      />
      <div className={styles.panes}>
        <div>
          <div className="strong">Select your shipment carrier:</div>
          {carriers.map((carrier, i) => (
            <Carrier
              key={i}
              carrier={carrier}
              selectedOption={selectedOption || i === 0}
              onSelected={option => setSelectedOption(option)}
            />
          ))}
        </div>
        <div>
          <div className={styles.buttonsRow}>
            <Button type="secondary" size="small" onClick={cloneShipment}>
              <IconCopyClipboard />
              Clone Shipment
            </Button>
            <Tooltip title="The printed/downloaded pdf will have 4 pallet labels per pallet, one for each side">
              <Button type="secondary" size="small" disableWhileOnClick={true} onClick={() => printPalletLabels()}>
                <IconPrintGoogle /> Print Pallet Labels
              </Button>
            </Tooltip>
          </div>
          {currentBatch.shipments.map((shipment, i) => <Shipment key={i} index={i} shipment={shipment} />)}
        </div>
      </div>
    </PageContent>
  );
}

interface CarrierProps {
  carrier: CarrierOptions;
  selectedOption: CarrierOption | boolean | null;
  onSelected: (carrier: CarrierOption) => void;
}

function Carrier({carrier, selectedOption, onSelected}: CarrierProps) {
  if (!selectedOption || carrier.options.length < 1) return null;

  const sortedOptions = useMemo(() => carrier.options.toSorted((l, r) => new Date(l.startTime).valueOf() - new Date(r.startTime).valueOf()), [carrier.options]);
  const cheapestOption = useMemo(() => sortedOptions.toSorted((l, r) => l.cost - r.cost)[0], [sortedOptions]);
  
  useEffect(() => {
    if (selectedOption === true) onSelected(cheapestOption);
  }, [selectedOption]);
  
  if (selectedOption === true) return null;
  
  const formatDate = date => dayjs(date).format("ddd MMMM D");
  const minCost = cheapestOption.cost;
  const defaultOption = selectedOption?.alphaCode === carrier.alphaCode ? selectedOption : cheapestOption
  const setSelected = (option: CarrierOption = defaultOption) => onSelected(option);
  return (
    <Card active={selectedOption?.alphaCode === carrier.alphaCode}>
      <CardContent
        style={{ display: "flex", cursor: "pointer", alignItems: "center", gap: "16px" }}
        onClick={() => setSelected()}
      >
        <RadioInput checked={selectedOption?.alphaCode === carrier.alphaCode} />
        <span className="strong">{carrier.name}</span>
        <span style={{flex: 1}}>{formatDate(defaultOption.startTime)}</span>
        {defaultOption.cost === minCost
          ? <span className="strong inline-positive"><IconCheckCircle />&nbsp;{formatValueAsCurrency(defaultOption.cost)}</span>
          : <span className="strong inline-neutral">{formatValueAsCurrency(defaultOption.cost)}</span>
        }
      </CardContent>
      <CardAccordion collapsed={selectedOption?.alphaCode !== carrier.alphaCode}>
        <CardContent style={{display: "flex", flexFlow: "wrap", gap: "16px"}}>
          {sortedOptions.map((option, i) => (
            <Card key={i} style={{minWidth: "fit-content", cursor: "pointer"}} onClick={() => onSelected(option)}>
              <CardContent style={{display: "flex", gap: "10px", padding: "4px 14px"}}>
                <RadioInput checked={option === selectedOption}/>
                <div>
                  <div style={{whiteSpace: "nowrap"}}>{formatDate(option.startTime)}</div>
                  <div className="strong" style={{whiteSpace: "nowrap"}}>{formatValueAsCurrency(option.cost)}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </CardAccordion>
    </Card>
  );
}

interface ShipmentProps {
  shipment: FbaShipment;
  index: number;
}

function Shipment({shipment, index}: ShipmentProps) {
  const { currentBatch } = useShipmentContext();
  const [collapsed, setCollapsed] = useState(false);
  const boxes = useMemo(() => currentBatch.boxes.filter(box => box.shipmentId === shipment.id), [shipment, currentBatch?.boxes]);
  const boxesCount = boxes.length;
  const [skusCount, unitsCount] = useMemo(() => [
    boxes.reduce((acc, box) => { box.packingItems.forEach(item => acc.add(item.sku)); return acc }, new Set).size,
    boxes.reduce((acc, box) => acc + box.packingItems.reduce((acc, item) => acc + item.quantity, 0), 0),
  ], [shipment.boxes]);
  return (
    <Card>
      <CardContent borderBottom="solid" style={{display: "flex", alignItems: "center"}}>
        <div style={{flex: 1}}>
          <div style={{display: "flex", alignItems: "center"}}>
            <span className="strong">Shipment {index + 1}&nbsp;</span>
            <span className="weak x-small-text">
              | {boxesCount} {boxesCount === 1 ? "Box " : "Boxes "}
              | {unitsCount} {unitsCount === 1 ? "Unit " : "Units "}
              | {skusCount} {skusCount === 1 ? "SKU" : "SKUs"}
            </span>
          </div>
          <div>
            <span className="strong">Amazon {shipment.destinationAddress.name}&nbsp;</span>
            <span className="weak small-text">
              {shipment.destinationAddress.address1},&nbsp;
              {shipment.destinationAddress.city},&nbsp;
              {shipment.destinationAddress.state}&nbsp;
              {shipment.destinationAddress.zipcode}&nbsp;
            </span>
          </div>
        </div>
        <CollapseButton collapsed={collapsed} onChange={setCollapsed}/>
      </CardContent>
      <CardAccordion collapsed={collapsed}>
        {(() => {
          const rows = [];
          let key = 0;
          for (const box of boxes) {
            rows.push(
              <CardContent key={key++} borderBottom="none" borderTop="solid">
                <BoxHeader box={box} shipment={shipment} />
              </CardContent>,
              ...box.packingItems.map((item) => (
                <CardContent key={key++}>
                  <ProductView small={true} item={getFbaBatchItemById(currentBatch, item.batchItemId)} quantity={item.quantity}/>
                </CardContent>
              ))
            )
          }
          return rows;
        })()}
      </CardAccordion>
    </Card>
  );
}
